'use client';

/**
 * Surfaced on a board page whose composite grid id appears in the
 * `banned_grids` table. The takeover-app lobby filters banned
 * boards out of every curation lane (curated + community +
 * activity ticker) client-side via `useLobbyBoards` /
 * `useGlobalActivity`, but the per-board page deliberately remains
 * reachable so seat holders can manage their positions and the
 * team can review the grid. (The API itself returns banned rows
 * with `banned: true` stamped on them so SDK builders can decide
 * their own moderation UX.)
 *
 * This banner is the visual anchor that warns viewers — particularly
 * anyone who arrived via a direct link — that the board has been
 * flagged by a moderator. It mirrors the visual language of
 * `BoardInitializingBanner` (compact, full-bleed inside its column,
 * backdrop-blurred) but uses a red/amber tone so the moderator
 * warning reads as more severe than the cosmetic "still warming
 * up" notice.
 */

import { ShieldAlert } from 'lucide-react';

import { cn } from '@/lib/utils';

interface BoardBannedBannerProps {
  /** Toggle from `gridPage.grid?.banned`. Component self-conceals when false. */
  banned: boolean | undefined | null;
  className?: string;
}

export function BoardBannedBanner({ banned, className }: BoardBannedBannerProps) {
  if (!banned) return null;

  return (
    <div
      className={cn(
        'rounded-[6px] border border-loss-red/40 bg-loss-red/10 backdrop-blur-md p-3 text-[11px] text-rose-50 space-y-1.5',
        className,
      )}
      role="alert"
    >
      <div className="flex items-start gap-2">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-loss-red" />
        <div className="space-y-1">
          <p className="font-mono uppercase tracking-[0.2em] text-loss-red text-[9px]">
            Flagged by moderator
          </p>
          <p className="text-rose-50/90">
            This board has been flagged by a moderator. Make sure you trust the creator.
          </p>
        </div>
      </div>
    </div>
  );
}
